import { useState } from 'react'
import { UserPlus } from 'lucide-react'
import { ACTION_ROW_CLASS, ActionRowBody, ActionRowChevron } from '@/components/action-items-table'
import { CancellationActionComplete } from '@/components/cancellation-action-complete'
import { FOLD_COUNT, FoldToggle } from '@/features/projects/fold-toggle'
import { isWorkedOnProject, roleLabel } from '@/features/purchase-orders/action-card-model'
import { actionTargetProps } from '@/hooks/use-action-focus'
import { cn } from '@/lib/utils'
import type { components } from '@/api/schema'

type ProjectAction = components['schemas']['ProjectOpenActionRead']
type StaffUser = components['schemas']['UserRead']

/** The people holding each role on the project, so each role's items show who is responsible. */
export interface ProjectTeam {
  sales_person?: StaffUser | null
  ops_person?: StaffUser | null
  warehousing_person?: StaffUser | null
  accounting_person?: StaffUser | null
}

const ROLE_PERSON: Record<string, keyof ProjectTeam> = {
  sales: 'sales_person',
  ops: 'ops_person',
  warehousing: 'warehousing_person',
  accounting: 'accounting_person',
}

/** Every project shows these four, in this order, even with nothing open for them. */
const TEAM_ROLES = ['sales', 'ops', 'accounting', 'warehousing'] as const

// Wide enough for "Overdue Oct 16" on one line.
const ROW_COLUMNS = 'grid-cols-[6rem_minmax(0,1fr)_auto]'

const PRIORITY_RANK = { high: 0, normal: 1, low: 2 } as const

// A row is highlighted in place, not ringed: the list's rows sit edge to edge.
const ROW_FOCUS_CLASS =
  'scroll-mt-24 outline-none transition-colors duration-500 data-[focused=true]:bg-sky-50 dark:data-[focused=true]:bg-sky-950/40'

/** Most urgent first: priority, then the soonest due date (overdue first, undated last), then the newest. */
function compareActions(a: ProjectAction, b: ProjectAction) {
  return (
    PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
    (a.expected_date ?? '9999').localeCompare(b.expected_date ?? '9999') ||
    b.created_at.localeCompare(a.created_at)
  )
}

function roleOf(action: ProjectAction) {
  const role = action.assigned_role
  return role === 'operations' ? 'ops' : (role ?? 'unassigned')
}

interface RoleGroup {
  role: string
  actions: ProjectAction[]
}

/** The four team roles in order, then any other role with items; each role's items most urgent first. */
function groupByRole(actions: ProjectAction[]): RoleGroup[] {
  const groups = new Map<string, ProjectAction[]>(TEAM_ROLES.map((role) => [role, []]))
  for (const action of [...actions].sort(compareActions)) {
    const role = roleOf(action)
    groups.set(role, [...(groups.get(role) ?? []), action])
  }
  return [...groups].map(([role, items]) => ({ role, actions: items }))
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

/** Who is responsible: the role and the person holding it on this project, as on the team pills. */
function RoleHeading({ role, team, count }: { role: string; team: ProjectTeam; count: number }) {
  const field = ROLE_PERSON[role]
  const person = field ? team[field] : null
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        aria-hidden
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
          person ? 'bg-primary text-primary-foreground' : 'border border-dashed text-muted-foreground',
        )}
      >
        {person ? <span className="initials">{initials(person.name)}</span> : <UserPlus className="size-3" />}
      </span>
      <span className="text-muted-foreground">{role === 'unassigned' ? 'No role' : roleLabel(role)}</span>
      <span className={cn('font-medium', !person && 'text-muted-foreground')}>{person?.name ?? 'Unassigned'}</span>
      {count > 0 && <span className="ml-auto text-xs tabular-nums text-muted-foreground">{count}</span>}
    </div>
  )
}

function ActionRow({ action, projectId }: { action: ProjectAction; projectId: string }) {
  // Items worked right here (PO review, the cancellation follow-up) stay on this page.
  if (isWorkedOnProject(action)) {
    return (
      <li
        {...actionTargetProps(action.uuid)}
        className={cn('grid items-start gap-x-3 px-3 py-2.5', ROW_COLUMNS, ROW_FOCUS_CLASS)}
      >
        <ActionRowBody action={action} />
        {action.action_source === 'project_cancellation' ? (
          <CancellationActionComplete projectUuid={projectId} />
        ) : (
          <span />
        )}
      </li>
    )
  }
  return (
    <li {...actionTargetProps(action.uuid)} className={cn(ACTION_ROW_CLASS, ROW_COLUMNS, ROW_FOCUS_CLASS)}>
      <ActionRowBody action={action} projectUuid={projectId} />
      <ActionRowChevron />
    </li>
  )
}

/** One role's items: the first three, the rest behind its own "Show all". */
function RoleSection({
  group,
  team,
  projectId,
  focusUuid,
  listId,
}: {
  group: RoleGroup
  team: ProjectTeam
  projectId: string
  focusUuid?: string
  listId: string
}) {
  const [expandedChoice, setExpandedChoice] = useState<boolean | null>(null)
  const focusIndex = focusUuid ? group.actions.findIndex((action) => action.uuid === focusUuid) : -1
  const expanded = expandedChoice ?? focusIndex >= FOLD_COUNT
  const shown = expanded ? group.actions : group.actions.slice(0, FOLD_COUNT)
  return (
    <section aria-label={`${roleLabel(group.role)} actions`} className="space-y-2">
      <RoleHeading role={group.role} team={team} count={group.actions.length} />
      {group.actions.length === 0 ? (
        <p className="pl-8 text-sm text-muted-foreground">None</p>
      ) : (
        <>
          <ul id={listId} className="divide-y overflow-hidden rounded-lg border bg-background">
            {shown.map((action) => (
              <ActionRow key={action.uuid} action={action} projectId={projectId} />
            ))}
          </ul>
          {group.actions.length > FOLD_COUNT && (
            <FoldToggle
              expanded={expanded}
              total={group.actions.length}
              noun="actions"
              controls={listId}
              onToggle={() => setExpandedChoice(!expanded)}
            />
          )}
        </>
      )}
    </section>
  )
}

/**
 * The project's open actions under each team role (Sales, Ops, Accounting, Warehouse), most urgent first. An action
 * opened by deep link (`focusUuid`) past its role's first three starts that role expanded.
 */
export function ProjectActionList({
  actions,
  projectId,
  team,
  focusUuid,
  listId,
}: {
  actions: ProjectAction[]
  projectId: string
  team: ProjectTeam
  focusUuid?: string
  listId: string
}) {
  return (
    <div id={listId} className="space-y-4">
      {groupByRole(actions).map((group) => (
        <RoleSection
          key={group.role}
          group={group}
          team={team}
          projectId={projectId}
          focusUuid={focusUuid}
          listId={`${listId}-${group.role}`}
        />
      ))}
    </div>
  )
}
